import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { COLLECTION } from "../../../../data/constant";

const RANGE_OPTIONS = [7, 15, 30, 90, 180];
const COLORS = ["#1976d2", "#00C49F", "#FFBB28", "#FF8042", "#AA336A", "#663399"];

const cardStyle = {
  p: 2,
  borderRadius: 3,
  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 200,
  fontFamily: "'Montserrat', sans-serif",
};

const formatDate = (d) => d.toISOString().split("T")[0];
const parseTimestamp = (ts) => (ts?.toDate ? ts.toDate() : new Date(ts));

const InventoryTab = () => {
  const [loads, setLoads] = useState([]);
  const [range, setRange] = useState(30);
  const [selectedPod, setSelectedPod] = useState("ALL");

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      const snap = await getDocs(collection(db, COLLECTION.CART_LOADING));
      setLoads(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => d.noLoad === false && Array.isArray(d.items))
      );
    };
    fetchData();
  }, []);

  /* ---------------- POD LIST ---------------- */
  const podList = useMemo(() => {
    const set = new Set(loads.map(l => l.podNumber));
    return ["ALL", ...Array.from(set)];
  }, [loads]);

  /* ---------------- KPI CALCULATIONS ---------------- */

  const activePods = useMemo(() => {
    const set = new Set(loads.map(l => l.podNumber));
    return set.size;
  }, [loads]);

  const totalInventoryAmount = useMemo(() =>
    loads.reduce((sum, l) => sum + Number(l.totalPrice || 0), 0)
  , [loads]);

  const todaysInventoryAmount = useMemo(() => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    return loads
      .filter(l => {
        const d = parseTimestamp(l.timestamp);
        return d >= today && d < tomorrow;
      })
      .reduce((sum, l) => sum + Number(l.totalPrice || 0), 0);
  }, [loads]);

  /* ---------------- DATE RANGE ---------------- */
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setDate(end.getDate() - range + 1);
    start.setHours(0, 0, 0, 0);
    return { startDate: start, endDate: end };
  }, [range]);

  /* ---------------- FILTERED LOADS (DATE + POD) ---------------- */
  const filteredLoads = useMemo(() => {
    return loads.filter(l => {
      const d = parseTimestamp(l.timestamp);
      if (d < startDate || d > endDate) return false;
      if (selectedPod !== "ALL" && l.podNumber !== selectedPod) return false;
      return true;
    });
  }, [loads, startDate, endDate, selectedPod]);

  /* ---------------- DAILY TREND ---------------- */
  const dailyTrend = useMemo(() => {
    const map = {};
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate()+1)) {
      map[formatDate(new Date(d))] = {
        x: formatDate(new Date(d)),
        value: 0,
        units: 0
      };
    }

    filteredLoads.forEach(l => {
      const key = formatDate(parseTimestamp(l.timestamp));
      map[key].value += Number(l.totalPrice || 0);
      l.items.forEach(i => {
        map[key].units += Number(i.quantity || 0);
      });
    });

    return Object.values(map);
  }, [filteredLoads, startDate, endDate]);

  /* ---------------- POD DISTRIBUTION ---------------- */
  const podDistribution = useMemo(() => {
    const map = {};
    filteredLoads.forEach(l => {
      if (!map[l.podNumber]) {
        map[l.podNumber] = { name: l.podNumber, value: 0 };
      }
      map[l.podNumber].value += Number(l.totalPrice || 0);
    });
    return Object.values(map);
  }, [filteredLoads]);

  return (
    <Box sx={{ mt: 2 }}>

      {/* KPI CARDS */}
      <Grid container spacing={2} justifyContent="space-between">
        {[
          ["Active Pods", activePods],
          ["Total Inventory Amount", `₹${totalInventoryAmount}`],
          ["Today's Inventory Amount", `₹${todaysInventoryAmount}`],
        ].map(([label, value]) => (
          <Grid item xs="auto" key={label}>
            <Paper sx={cardStyle}>
              <Typography variant="subtitle2" color="textSecondary">
                {label}
              </Typography>
              <Typography variant="h6">{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* CHARTS */}
      <Grid container spacing={3} sx={{ mt: 2 }}>

        {/* Inventory Value Trend */}
        <Grid item xs={12} md="auto" sx={{ flex:1, minWidth:300 }}>
          <Paper sx={{ p:2, height:350 }}>
            <Box sx={{ display:"flex", justifyContent:"space-between", gap:1 }}>
              <Typography variant="subtitle2">Inventory Value Trend</Typography>

              <Box sx={{ display:"flex", gap:1 }}>
                <FormControl size="small">
                  <Select value={selectedPod} onChange={e => setSelectedPod(e.target.value)}>
                    {podList.map(p => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <Select value={range} onChange={e => setRange(+e.target.value)}>
                    {RANGE_OPTIONS.map(r => (
                      <MenuItem key={r} value={r}>{r}d</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip formatter={(v)=>`₹${v}`} />
                <Line dataKey="value" stroke="#1976d2" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Units Trend */}
        <Grid item xs={12} md="auto" sx={{ flex:1, minWidth:300 }}>
          <Paper sx={{ p:2, height:350 }}>
            <Typography variant="subtitle2">Units Loaded Trend</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="units" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Inventory by Pod */}
        <Grid item xs={12} md="auto" sx={{ flex:1, minWidth:350 }}>
          <Paper sx={{ p:2, height:350 }}>
            <Typography variant="subtitle2">Inventory by Pod</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={podDistribution}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={100}
                  label
                >
                  {podDistribution.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v)=>`₹${v}`} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

export default InventoryTab;
